import { useEffect, useRef, useState } from "react";
import { SwitchCamera, X } from "lucide-react";
import { CHAT_MAX_VIDEO_UPLOAD_BYTES } from "@/lib/chatApi";
import { formatVoiceClock, stopChatVoiceStream } from "@/lib/chatVoice";

type Mode = "photo" | "video";

type Props = {
  open: boolean;
  onClose: () => void;
  onPhoto: (file: File) => void;
  onVideo: (file: File) => void;
  onFallback: () => void;
};

const VIDEO_MIME = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

const MAX_VIDEO_MS = 45_000;

function pickVideoMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return VIDEO_MIME.find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
}

function stampName(prefix: string, ext: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `${prefix}-${stamp}.${ext}`;
}

export function ChatCaptureSheet({ open, onClose, onPhoto, onVideo, onFallback }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<number | null>(null);
  const facingRef = useRef<"environment" | "user">("environment");
  const aliveRef = useRef(true);
  const [mode, setMode] = useState<Mode>("photo");
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recMs, setRecMs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const stopTick = () => {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const stopStream = () => {
    stopTick();
    try {
      recRef.current?.stop();
    } catch {
      /* ignore */
    }
    recRef.current = null;
    stopChatVoiceStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const startStream = async (facing: "environment" | "user") => {
    stopChatVoiceStream(streamRef.current);
    streamRef.current = null;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: {
        facingMode: { ideal: facing },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });
    streamRef.current = stream;
    facingRef.current = facing;
    const el = videoRef.current;
    if (el) {
      el.srcObject = stream;
      await el.play().catch(() => undefined);
    }
    setReady(true);
  };

  useEffect(() => {
    if (!open) {
      aliveRef.current = false;
      stopStream();
      setReady(false);
      setRecording(false);
      setRecMs(0);
      setBusy(false);
      setErr(null);
      setMode("photo");
      return;
    }
    aliveRef.current = true;
    if (!navigator.mediaDevices?.getUserMedia) {
      onFallback();
      return;
    }
    let dead = false;
    setBusy(true);
    void startStream("environment")
      .catch(() => {
        if (!dead) onFallback();
      })
      .finally(() => {
        if (!dead) setBusy(false);
      });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !recRef.current) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      dead = true;
      aliveRef.current = false;
      window.removeEventListener("keydown", onKey);
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open only
  }, [open]);

  const snapPhoto = async () => {
    const el = videoRef.current;
    if (!el || !ready || busy || recording) return;
    const w = el.videoWidth;
    const h = el.videoHeight;
    if (!w || !h) return;
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas");
      ctx.drawImage(el, 0, 0, w, h);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("blob"))), "image/jpeg", 0.9);
      });
      onPhoto(new File([blob], stampName("photo", "jpg"), { type: "image/jpeg" }));
      onClose();
    } catch {
      setErr("Impossible de prendre la photo.");
    } finally {
      setBusy(false);
    }
  };

  const startRec = () => {
    const stream = streamRef.current;
    if (!stream || recording || busy) return;
    const mime = pickVideoMime();
    if (!mime && typeof MediaRecorder === "undefined") {
      setErr("Enregistrement vidéo non disponible sur cet appareil.");
      return;
    }
    chunksRef.current = [];
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    recRef.current = rec;
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      stopTick();
      setRecording(false);
      recRef.current = null;
      if (!aliveRef.current) return;
      const type = rec.mimeType || mime || "video/webm";
      const ext = type.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (blob.size < 800) {
        setErr("Vidéo trop courte.");
        return;
      }
      if (blob.size > CHAT_MAX_VIDEO_UPLOAD_BYTES) {
        setErr("Vidéo trop lourde (max 50 Mo). Filmez un peu moins longtemps.");
        return;
      }
      onVideo(new File([blob], stampName("video", ext), { type }));
      onClose();
    };
    rec.start(250);
    setRecording(true);
    setRecMs(0);
    const started = Date.now();
    tickRef.current = window.setInterval(() => {
      const ms = Date.now() - started;
      setRecMs(ms);
      if (ms >= MAX_VIDEO_MS) {
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
      }
    }, 200);
  };

  const stopRec = () => {
    try {
      recRef.current?.stop();
    } catch {
      /* ignore */
    }
  };

  const shutter = () => {
    if (mode === "photo") void snapPhoto();
    else if (recording) stopRec();
    else startRec();
  };

  const flip = async () => {
    if (recording || busy) return;
    setBusy(true);
    try {
      await startStream(facingRef.current === "environment" ? "user" : "environment");
    } catch {
      setErr("Impossible de changer de caméra.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="chat-capture" role="dialog" aria-modal="true" aria-label="Appareil photo">
      <button
        type="button"
        className="chat-photo-filter__close"
        disabled={recording}
        aria-label="Fermer"
        onClick={onClose}
      >
        <X className="size-5" />
      </button>

      <video ref={videoRef} className="chat-capture__live" autoPlay playsInline muted />

      <div className="chat-capture__dock">
        {err ? <p className="chat-photo-filter__err">{err}</p> : null}
        {recording ? (
          <p className="chat-capture__timer" aria-live="polite">
            {formatVoiceClock(recMs)}
          </p>
        ) : null}
        <div className="chat-capture__modes">
          <button
            type="button"
            className={`chat-capture__mode ${mode === "photo" ? "is-on" : ""}`}
            disabled={recording}
            onClick={() => setMode("photo")}
          >
            Photo
          </button>
          <button
            type="button"
            className={`chat-capture__mode ${mode === "video" ? "is-on" : ""}`}
            disabled={recording}
            onClick={() => setMode("video")}
          >
            Vidéo
          </button>
        </div>
        <div className="chat-capture__row">
          <span className="chat-capture__spacer" />
          <button
            type="button"
            className={`chat-capture__shutter ${recording ? "is-rec" : ""} ${mode === "video" ? "is-video" : ""}`}
            disabled={!ready || busy}
            aria-label={mode === "photo" ? "Prendre la photo" : recording ? "Arrêter" : "Filmer"}
            onClick={shutter}
          />
          <button
            type="button"
            className="chat-capture__flip"
            disabled={recording || busy}
            aria-label="Retourner la caméra"
            onClick={() => void flip()}
          >
            <SwitchCamera className="size-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
